import React, { useEffect, useMemo, useState } from 'react';
import { Mui } from '@leap4/frontend';
import { IImportToAllResult, ITrackedPublisher } from './appAdsLines.interface';
import {
    IRowIssue,
    downloadLinesTemplate,
    importCsvToAll,
    previewCsvToAll,
    rejectionFromError,
} from './appAdsLines.api';
import { RowIssueList } from './rowIssues';
import { readSheetRows } from './readSheet';
import { errorMessage } from './appAdsLines.api';

const {
    Alert,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    LinearProgress,
    TextField,
    Tooltip,
    Typography,
} = Mui;

interface ImportToAllModalProps {
    open: boolean;
    publishers: ITrackedPublisher[];
    onClose: () => void;
    /** Called with a summary once lines have been added, so the caller can refresh and report. */
    onDone: (summary: string) => void;
}

/**
 * Adds one set of lines to many publishers at once.
 *
 * The case this exists for: you sign a new buyer, and every publisher in the portfolio now
 * needs that buyer's line. Without this you'd open each publisher and paste the same lines
 * forty times.
 *
 * Lines can come from a paste, a file, or both at once - they're combined into a single set,
 * matching the prototype's behaviour. Every publisher is selected by default because "all of
 * them" is the normal case; deselecting is the exception.
 *
 * Duplicates are resolved per publisher server-side, so a line that already exists for some
 * publishers only lands where it's missing. That's what makes this safe to re-run.
 */
export const ImportToAllModal: React.FC<ImportToAllModalProps> = ({ open, publishers, onClose, onDone }) => {
    const [pasted, setPasted] = useState('');
    const [fileRows, setFileRows] = useState<string[][]>([]);
    const [fileName, setFileName] = useState('');
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState<(IImportToAllResult & { rowsRead?: number; invalid?: number }) | null>(null);
    const [error, setError] = useState('');
    const [preview, setPreview] = useState<{ rowsRead: number; understood: number; issues: IRowIssue[] } | null>(null);
    const [issues, setIssues] = useState<IRowIssue[]>([]);

    /**
     * Reset only when the dialog opens.
     *
     * This deliberately does NOT depend on `publishers`: adding lines invalidates the publisher
     * query, which produced a new array, which re-ran this effect and wiped the user's paste and
     * the success message out from under them while the dialog stayed open.
     */
    useEffect(() => {
        if (!open) return;
        setPasted('');
        setFileRows([]);
        setFileName('');
        setResult(null);
        setError('');
        setPreview(null);
        setIssues([]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Pre-select everyone once the list is known - rolling a new buyer out to all publishers is
    // the normal case. Kept separate from the reset above so a refetch can't clear the form.
    useEffect(() => {
        if (open && publishers.length) {
            setSelected((prev) => (prev.size ? prev : new Set(publishers.map((p) => p._id))));
        }
    }, [open, publishers]);

    /**
     * Paste and file are merged into one list of single-cell rows. The server parses them with
     * the same app-ads.txt parser used everywhere else, so both shapes behave identically and
     * unparseable rows are dropped rather than saved malformed.
     */
    const combinedRows: string[][] = useMemo(() => {
        const pastedRows = pasted
            .split(/\r?\n/)
            .map((l) => l.trim())
            .filter((l) => l !== '' && !l.startsWith('#'))
            .map((l) => [l]);
        return [...fileRows, ...pastedRows];
    }, [pasted, fileRows]);

    const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            setError('');
            setFileName(file.name);
            setPreview(null);
            setIssues([]);
            setFileRows(await readSheetRows(file));
        } catch (err: any) {
            setError(`Could not read the file: ${errorMessage(err)}`);
        }
    };

    const toggleAll = () =>
        setSelected((prev) => (prev.size === publishers.length ? new Set() : new Set(publishers.map((p) => p._id))));

    const toggleOne = (id: string) =>
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });

    /** Validates the combined paste + file before anything is written to any publisher. */
    const check = async () => {
        if (!combinedRows.length) {
            setPreview(null);
            return;
        }
        try {
            setBusy(true);
            setError('');
            setIssues([]);
            const p = await previewCsvToAll(combinedRows);
            setPreview({ rowsRead: p.rowsRead, understood: p.understood, issues: p.issues || [] });
        } catch (e: any) {
            setError(errorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    const submit = async () => {
        try {
            setBusy(true);
            setError('');
            setIssues([]);
            // Raw rows go up as-is; the server validates and parses them with the same rules the
            // single-publisher path uses, so behaviour is identical either way.
            const res = await importCsvToAll(combinedRows, Array.from(selected));
            setResult(res);

            const touched = res.perPublisher?.filter((p) => p.added > 0).length ?? 0;
            onDone(
                res.added
                    ? `Added ${res.added} line(s) across ${touched} publisher(s). Skipped ${res.skipped} already present.`
                    : `Nothing added — all ${res.skipped} line(s) were already present.`,
            );
            // Close on success: leaving it open with a stale form invited a second submit.
            onClose();
        } catch (e: any) {
            const rejection = rejectionFromError(e);
            if (rejection) setIssues(rejection.issues);
            else setError(errorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    const allSelected = publishers.length > 0 && selected.size === publishers.length;
    // A single unreadable row blocks the whole rollout: applying a partial list across forty
    // publishers would be forty places to later discover something was quietly missing.
    const blocked = !!preview && preview.issues.length > 0;
    const canSubmit =
        !busy && !!combinedRows.length && !!selected.size && !result && !blocked && !issues.length && !!preview;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>Add lines to publishers</DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    For rolling a new buyer's lines out across your portfolio. Paste lines, upload a file, or both —
                    they're combined. Publishers that already have a line keep just the one copy.
                </Typography>

                <Box display="flex" gap={1} alignItems="center" sx={{ mb: 2 }}>
                    <Button variant="outlined" component="label" size="small">
                        Upload CSV
                        <input type="file" accept=".csv, .xlsx, .xls" hidden onChange={onFile} />
                    </Button>
                    <Button size="small" onClick={downloadLinesTemplate}>
                        Download template
                    </Button>
                    {fileName && (
                        <Typography variant="caption" color="text.secondary">
                            {fileName} — {fileRows.length} row(s)
                        </Typography>
                    )}
                </Box>

                <TextField
                    fullWidth
                    multiline
                    minRows={6}
                    label="Or paste lines"
                    value={pasted}
                    onChange={(e) => {
                        setPasted(e.target.value);
                        setPreview(null);
                        setIssues([]);
                    }}
                    onBlur={check}
                    placeholder={'pubmatic.com, 161599, RESELLER, 5d62403b186f2ace\nconversantmedia.com, 100353, RESELLER'}
                    InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
                />

                <Divider sx={{ my: 2 }} />

                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle2">Apply to</Typography>
                    <FormControlLabel
                        control={<Checkbox size="small" checked={allSelected} onChange={toggleAll} />}
                        label="All publishers"
                    />
                </Box>

                <Box
                    display="grid"
                    gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }}
                    sx={{ maxHeight: 220, overflowY: 'auto' }}
                >
                    {publishers.map((p) => (
                        <FormControlLabel
                            key={p._id}
                            control={
                                <Checkbox size="small" checked={selected.has(p._id)} onChange={() => toggleOne(p._id)} />
                            }
                            label={
                                <span>
                                    {p.name}{' '}
                                    <Typography component="span" variant="caption" color="text.secondary">
                                        ({p.domain})
                                    </Typography>
                                </span>
                            }
                        />
                    ))}
                </Box>

                {!publishers.length && (
                    <Typography variant="body2" color="text.secondary">
                        No publishers tracked yet.
                    </Typography>
                )}

                {busy && <LinearProgress sx={{ mt: 2 }} />}

                {preview && !blocked && !result && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        {preview.rowsRead} line(s) read, all readable. Nothing is written until you press Add lines.
                    </Alert>
                )}

                <RowIssueList
                    issues={preview?.issues || []}
                    rowsRead={preview?.rowsRead}
                    title={
                        preview && preview.issues.length
                            ? `${preview.issues.length} of ${preview.rowsRead} line(s) can't be read — fix these before adding`
                            : undefined
                    }
                />
                <RowIssueList issues={issues} />

                {result && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                        Added {result.added} line(s) across {selected.size} publisher(s). Skipped {result.skipped}{' '}
                        already present.
                        {!!result.perPublisher?.length && (
                            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                                {result.perPublisher
                                    .filter((p) => p.added > 0)
                                    .map((p) => (
                                        <li key={p.publisherId}>
                                            {p.name}: {p.added} added
                                        </li>
                                    ))}
                            </Box>
                        )}
                    </Alert>
                )}
                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto', pl: 2 }}>
                    {combinedRows.length} line(s) · {selected.size} publisher(s)
                </Typography>
                <Button onClick={onClose}>{result ? 'Done' : 'Cancel'}</Button>
                {!preview && !result && (
                    <Tooltip
                        title={combinedRows.length ? '' : 'Add or paste at least one line first'}
                    >
                        {/* A disabled MUI button swallows pointer events, so the tooltip needs a
                            live wrapper to attach to. */}
                        <span>
                            <Button onClick={check} disabled={busy || !combinedRows.length}>
                                Check
                            </Button>
                        </span>
                    </Tooltip>
                )}
                <Tooltip
                    title={
                        canSubmit
                            ? ''
                            : !combinedRows.length
                            ? 'Add or paste at least one line first'
                            : blocked || issues.length
                            ? 'Fix the rows listed above before adding'
                            : !preview
                            ? 'Press Check first — lines are validated before anything is written'
                            : !selected.size
                            ? 'Select at least one publisher'
                            : ''
                    }
                >
                    <span>
                        <Button variant="contained" onClick={submit} disabled={!canSubmit}>
                            Add lines
                        </Button>
                    </span>
                </Tooltip>
            </DialogActions>
        </Dialog>
    );
};

export default ImportToAllModal;
